/**
 * Bluesky auth context — wraps `@atproto/oauth-client-browser` and
 * exposes a stable `{ isReady, isSignedIn, handle, did, agent,
 * signIn, signOut }` shape to the rest of the tree.
 *
 * Shared between the web PWA (Vite / React Router) and the
 * self-hosted app (Rex / SSR). Rex renders this on the server, so
 * every browser-only side effect is gated behind `useEffect`:
 *   - `BrowserOAuthClient` is imported via `await import(...)` on
 *     mount — the library crashes if loaded in a Node context.
 *   - `client.init()` is called once per mount.
 *   - SSR always sees `{ isReady: false, isSignedIn: false }` so
 *     the "Sign in to publish" placeholder renders identically on
 *     both passes and hydration is quiet.
 *
 * Client identity:
 *   - **Prod**: `https://pantryhost.app/client-metadata.json` is
 *     fetched via `BrowserOAuthClient.load({ clientId })`.
 *   - **Dev (localhost)**: the loopback form — `new
 *     BrowserOAuthClient({ clientMetadata: undefined })`. Requires
 *     the dev server to be reached via `http://127.0.0.1` (not
 *     `localhost`) — per atproto OAuth spec loopback rules.
 *
 * Auth path decision recorded in
 * `memory/project_bluesky_app_status.md` and
 * `packages/marketing/ATPROTO-TIER-1.5.md` — OAuth from day one,
 * no app passwords.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  ATPROTO_PUBLISH_OAUTH_SCOPE,
  DEFAULT_CLIENT_METADATA_URL,
} from '../atproto-client';

// Re-exported for back-compat: existing consumers import the scope from
// this context module. Its home is now the React-free ./atproto-client.
export { ATPROTO_PUBLISH_OAUTH_SCOPE };

// ── Types ────────────────────────────────────────────────────────────────

export interface BlueskyAuthState {
  /** False until the provider has finished its first init() call
   *  (i.e. we've either restored a session, processed an OAuth
   *  callback, or confirmed there was nothing to restore). */
  isReady: boolean;
  isSignedIn: boolean;
  did: string | null;
  handle: string | null;
  /** Raw `@atproto/api` Agent, ready to make authenticated calls.
   *  Null until the user is signed in. Typed as unknown here so
   *  the shared package doesn't hard-require `@atproto/api` at
   *  its call sites — the `atproto-publish` client does its own
   *  duck-typing via `PublishAgent`. */
  agent: unknown | null;
  /** Kick off the OAuth redirect. Never resolves in the happy
   *  path — the browser navigates away. Throws if not ready. */
  signIn: (handleInput: string) => Promise<void>;
  /** Sign in WITHOUT navigating this page: OAuth runs in a nested
   *  popup and the session lands here via BroadcastChannel. Required
   *  wherever navigation would destroy in-memory state — the publish
   *  broker popup MUST use this, because the PDS auth pages send COOP
   *  headers that permanently sever `window.opener` on a redirect
   *  round-trip, orphaning the broker from its requester. Resolves
   *  once signed in. Must be called from a user gesture. */
  signInPopup: (handleInput: string) => Promise<void>;
  /** Revoke tokens and drop local session state. */
  signOut: () => Promise<void>;
  /** Human-readable status for error toasts / debug. */
  error: string | null;
}

const DEFAULT_STATE: BlueskyAuthState = {
  isReady: false,
  isSignedIn: false,
  did: null,
  handle: null,
  agent: null,
  signIn: async () => {
    throw new Error('BlueskyAuthProvider not mounted');
  },
  signInPopup: async () => {
    throw new Error('BlueskyAuthProvider not mounted');
  },
  signOut: async () => {
    throw new Error('BlueskyAuthProvider not mounted');
  },
  error: null,
};

const BlueskyAuthContext = createContext<BlueskyAuthState>(DEFAULT_STATE);

// ── Client construction ──────────────────────────────────────────────────

/** Read the prod client-metadata URL — override-able so a self-hoster
 *  can point at THEIR OWN hosted `client-metadata.json` for OAuth
 *  sovereignty (see docs/self-hosted-oauth.md). Resolution order mirrors
 *  brokerUrl()/isDryRun(): Vite env (web build) → `atproto-client-id`
 *  meta tag (Rex — process.env isn't inlined into its client bundle) →
 *  process.env (bundlers that do inline it) → hosted pantryhost.app. */
function getProdClientId(): string {
  try {
    // Exact `import.meta.env.VITE_X` token required — Vite's env
    // replacement doesn't recognize optional-chained access; the
    // try/catch covers non-Vite bundlers.
    // @ts-expect-error Vite-ism
    const fromVite = import.meta.env.VITE_ATPROTO_CLIENT_ID;
    if (fromVite) return String(fromVite);
  } catch {
    /* not vite */
  }
  if (typeof document !== 'undefined') {
    const meta = document
      .querySelector('meta[name="atproto-client-id"]')
      ?.getAttribute('content');
    if (meta) return meta;
  }
  if (typeof process !== 'undefined' && process.env?.ATPROTO_CLIENT_ID) {
    return process.env.ATPROTO_CLIENT_ID;
  }
  return DEFAULT_CLIENT_METADATA_URL;
}

/** Loopback-safe or hosted-metadata-safe? We key off origin. The
 *  atproto OAuth spec requires the loopback path to use
 *  `127.0.0.1` / `[::1]` — `localhost` won't be accepted by the
 *  PDS authorization server. */
function isLoopbackOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return hostname === '127.0.0.1' || hostname === '[::1]' || hostname === '::1';
}

function isLocalhostOrigin(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost';
}

// ── Provider ─────────────────────────────────────────────────────────────

export interface BlueskyAuthProviderProps {
  children: ReactNode;
  /** Where the OAuth callback lives. Both packages put it at
   *  `/oauth/bluesky/callback`. Passed in so each package can
   *  mount it on its own router. */
  callbackPath?: string;
}

export function BlueskyAuthProvider({
  children,
  callbackPath = '/oauth/bluesky/callback',
}: BlueskyAuthProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [session, setSession] = useState<unknown | null>(null);
  const [did, setDid] = useState<string | null>(null);
  const [handle, setHandle] = useState<string | null>(null);
  const [agent, setAgent] = useState<unknown | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Hold the BrowserOAuthClient across renders. Stored in state
  // rather than a ref so we can await it in signIn/signOut via
  // closure — and so hot-reload gets a fresh one.
  const [client, setClient] = useState<any>(null);

  // One-shot init on mount. SSR sees isReady=false and renders
  // "Sign in" placeholders.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;

    (async () => {
      try {
        const { BrowserOAuthClient } = await import('@atproto/oauth-client-browser');
        const { Agent } = await import('@atproto/api');
        const url = new URL(window.location.href);
        const callbackAbsolute = `${url.origin}${callbackPath}`;

        let c: any;
        if (isLoopbackOrigin()) {
          // Loopback client — no hosted metadata. The library's
          // default `buildLoopbackClientId(window.location)` bakes
          // the current pathname into the client_id, which then
          // fails validation ("must not contain a path component")
          // when init() is triggered from any deep route. Work
          // around by constructing the loopback client_id from a
          // path-free location and providing the metadata up front.
          // Callback lands on `/` — the provider's init() handler
          // picks up OAuth params regardless of route.
          const { buildLoopbackClientId } = await import('@atproto/oauth-client-browser');
          const { atprotoLoopbackClientMetadata } = await import('@atproto/oauth-types');
          const loc = {
            hostname: window.location.hostname,
            port: window.location.port,
            pathname: '/',
          } as Location;
          // Bake the publish scopes into the loopback client_id —
          // the AS derives the client's allowed scopes from these
          // query params, so signInRedirect's `scope` must be
          // covered here or the token request is rejected.
          const bare = buildLoopbackClientId(loc);
          const clientIdUrl = new URL(bare);
          clientIdUrl.searchParams.set('scope', ATPROTO_PUBLISH_OAUTH_SCOPE);
          const clientId = clientIdUrl.href;
          const clientMetadata = (atprotoLoopbackClientMetadata as (cid: string) => unknown)(clientId);
          c = new BrowserOAuthClient({
            clientMetadata,
            handleResolver: 'https://bsky.social',
            allowHttp: true,
          } as any);
        } else if (isLocalhostOrigin()) {
          // `localhost` won't work with loopback-client OAuth. Warn
          // loudly rather than silently failing at signIn time.
          console.warn(
            '[BlueskyAuth] The atproto OAuth loopback client requires http://127.0.0.1 (or [::1]) — localhost will not be accepted. Reach this app via http://127.0.0.1:' +
              window.location.port +
              ' to sign in with Bluesky.',
          );
          setError(
            'Bluesky sign-in requires http://127.0.0.1 in dev. Open this app at 127.0.0.1 instead of localhost.',
          );
          setIsReady(true);
          return;
        } else {
          // Hosted client — fetch the metadata file.
          c = await BrowserOAuthClient.load({
            clientId: getProdClientId(),
            handleResolver: 'https://bsky.social',
          } as any);
        }
        if (cancelled) return;
        setClient(c);

        const result = await c.init();
        if (cancelled) return;

        if (result?.session) {
          const s = result.session;
          // When the result carries a `state` key, init() processed a
          // fresh OAuth callback (not a session restore) — at this
          // point the library has already rewritten the URL to strip
          // the `?code=...&state=...` params. This is our window to
          // send the user back to wherever they clicked Share-to-
          // Bluesky from, since the loopback OAuth flow always lands
          // on `/` regardless of the original route. We use
          // `location.replace` so the browser gets a fresh mount on
          // the target route and the restore-path populates the
          // session state cleanly — avoids double-setting state
          // while the current Provider is still mid-init.
          if ('state' in result) {
            let returnTo: string | null = null;
            try {
              returnTo = sessionStorage.getItem('bsky-return-to');
              if (returnTo) sessionStorage.removeItem('bsky-return-to');
            } catch {
              /* ignore */
            }
            if (returnTo && returnTo !== window.location.pathname + window.location.search + window.location.hash) {
              window.location.replace(returnTo);
              return;
            }
          }
          setSession(s);
          setDid(s.did);
          // Resolve the handle for display. Best-effort — the session
          // may not carry it, so fall back to the DID.
          try {
            const a = new Agent(s as any);
            setAgent(a);
            // Most PDSes expose the handle on the DID doc via
            // com.atproto.identity.resolveHandle inverse lookups;
            // easiest quick-path is a profile read.
            const profile = await a.com.atproto.repo.describeRepo({ repo: s.did });
            const resolved =
              (profile?.data as any)?.handle || s.did;
            if (!cancelled) setHandle(resolved);
          } catch {
            if (!cancelled) setHandle(s.did);
            if (!cancelled) setAgent(new Agent(s as any));
          }
        }
        if (!cancelled) setIsReady(true);
      } catch (err: any) {
        // We're the throwaway callback page of a signInPopup flow:
        // init() already handed the session to the opening window via
        // BroadcastChannel and the opener is about to close us. Not an
        // error — just don't flash one while we're being closed.
        if (err?.code === 'LOGIN_CONTINUED_IN_PARENT_WINDOW') return;
        if (!cancelled) {
          console.error('[BlueskyAuth] init failed', err);
          setError(err?.message ?? 'Failed to initialize Bluesky auth');
          setIsReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // callbackPath is effectively a constant per mount — intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callbackPath]);

  const signIn = useCallback(
    async (handleInput: string) => {
      if (!client) throw new Error('Bluesky auth not ready');
      const clean = handleInput.trim().replace(/^@/, '');
      if (!clean) throw new Error('Enter a Bluesky handle');
      // Stash where we came from so the callback can send us back.
      try {
        sessionStorage.setItem('bsky-return-to', window.location.pathname + window.location.search + window.location.hash);
      } catch {
        /* ignore */
      }
      // signInRedirect never resolves in the happy path — the
      // browser navigates away. Let the caller's promise stay
      // pending so any loading UI stays on.
      await client.signInRedirect(clean, {
        scope: ATPROTO_PUBLISH_OAUTH_SCOPE,
      });
    },
    [client],
  );

  const signInPopup = useCallback(
    async (handleInput: string) => {
      if (!client) throw new Error('Bluesky auth not ready');
      const clean = handleInput.trim().replace(/^@/, '');
      if (!clean) throw new Error('Enter a Bluesky handle');
      // No awaits before this call — signInPopup opens its window
      // synchronously and must still be inside the click gesture or
      // popup blockers eat it. Resolves (via BroadcastChannel from the
      // callback page) once the user finishes on the PDS; THIS page
      // never navigates.
      const s = await client.signInPopup(clean, {
        scope: ATPROTO_PUBLISH_OAUTH_SCOPE,
      });
      // Same session-application as the init() restore path.
      setSession(s);
      setDid(s.did);
      const { Agent } = await import('@atproto/api');
      const a = new Agent(s as any);
      setAgent(a);
      try {
        const profile = await a.com.atproto.repo.describeRepo({ repo: s.did });
        setHandle((profile?.data as any)?.handle || s.did);
      } catch {
        setHandle(s.did);
      }
    },
    [client],
  );

  const signOut = useCallback(async () => {
    if (!client || !did) return;
    try {
      await client.revoke(did);
    } catch {
      // best-effort — even if revoke fails locally we still drop state
    }
    setSession(null);
    setDid(null);
    setHandle(null);
    setAgent(null);
  }, [client, did]);

  const value = useMemo<BlueskyAuthState>(
    () => ({
      isReady,
      isSignedIn: Boolean(session),
      did,
      handle,
      agent,
      signIn,
      signInPopup,
      signOut,
      error,
    }),
    [isReady, session, did, handle, agent, signIn, signInPopup, signOut, error],
  );

  return <BlueskyAuthContext.Provider value={value}>{children}</BlueskyAuthContext.Provider>;
}

/** Consumer hook. Safe to call from SSR — returns the default
 *  (not-signed-in, not-ready) state if no provider is mounted,
 *  which matches the "in dev I'm not wrapping the tree yet"
 *  degraded experience without throwing. */
export function useBlueskyAuth(): BlueskyAuthState {
  return useContext(BlueskyAuthContext);
}
